import clsx from 'clsx'
import type { ComponentPropsWithoutRef, ReactElement } from 'react'

import { objectKeys } from '@lib/utils/object'

import { type Sources } from '@components/contentful-content-types/asset-image/utils'
import { Image } from '@components/ui-core/image/image'

import styles from './picture.module.css'

export interface PictureProps extends ComponentPropsWithoutRef<'picture'> {
  alt?: string
  src: string
  fetchPriority?: HTMLImageElement['fetchPriority']
  height?: number
  loading?: HTMLImageElement['loading']
  width?: number
  srcSets?: Sources
  sizes?: string[]
  responsive?: boolean
  fit?: 'contain' | 'cover'
  imgClassName?: string
}

export function Picture(props: PictureProps): ReactElement {
  const {
    alt = '',
    src,
    width,
    height,
    className,
    imgClassName,
    fetchPriority = 'low',
    loading = 'lazy',
    children,
    srcSets,
    sizes,
    responsive = true,
    fit = 'cover',
    ...rest
  } = props

  const classes = clsx(
    styles.picture,
    {
      [styles.responsive]: responsive,
    },
    className,
  )

  return (
    <picture className={classes} {...rest}>
      {srcSets && objectKeys(srcSets).length > 0
        ? objectKeys(srcSets).map(format => (
            <source
              key={format}
              srcSet={srcSets[format].join(', ')}
              sizes={sizes?.join(', ')}
              type={format == 'original' ? undefined : `image/${format}`}
            />
          ))
        : null}
      {/* Allows passing custom <Image /> */}
      {children ?? (
        <Image
          src={src}
          alt={alt}
          className={clsx(responsive ? styles.responsive : undefined, imgClassName)}
          width={width}
          height={height}
          loading={loading}
          fetchPriority={fetchPriority}
          sizes={sizes?.join(', ')}
          fit={fit}
        />
      )}
    </picture>
  )
}
